/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Stethoscope, ShieldCheck, Euro, CheckCircle2, Clock, Settings2 } from 'lucide-react';
import { updateMyDoctorProfile } from '@/lib/telemedicine';

/**
 * DoctorProfileCard — compact telemedicine profile editor (price, bio,
 * accepting bookings, subscription status).
 * Extracted from DoctorCommandCenter.jsx (Code Quality refactor).
 */
export const DoctorProfileCard = ({ profile, onSaved, onStartSubscription, subCheckingOut, onOpenAvailability }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    visit_price_eur: profile?.visit_price_eur ?? 25,
    consultation_bio: profile?.consultation_bio ?? '',
    accepting_bookings: profile?.accepting_bookings ?? true,
  });

  useEffect(() => {
    if (profile) {
      setForm({
        visit_price_eur: profile.visit_price_eur ?? 25,
        consultation_bio: profile.consultation_bio ?? '',
        accepting_bookings: profile.accepting_bookings ?? true,
      });
    }
  }, [profile]);

  const save = async () => {
    setSaving(true);
    try {
      await updateMyDoctorProfile({
        visit_price_eur: Number(form.visit_price_eur),
        consultation_bio: form.consultation_bio,
        accepting_bookings: form.accepting_bookings,
      });
      toast.success(t('telemedProfileSaved'));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
    } finally {
      setSaving(false);
    }
  };

  const active = profile?.subscription_active;
  const expDate = profile?.subscription_expiry_date ? new Date(profile.subscription_expiry_date).toLocaleDateString('it-IT') : null;

  return (
    <Card data-testid="doctor-profile-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="font-display flex items-center gap-2"><Stethoscope className="h-5 w-5 text-primary" /> {t('telemedDoctorAreaTitle')}</CardTitle>
            <CardDescription>{t('telemedDoctorAreaDesc')}</CardDescription>
          </div>
          <Badge variant={active ? 'success' : 'secondary'} data-testid="doctor-subscription-status">
            {active ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
            {active ? t('telemedActive') : t('telemedInactive')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!active && (
          <div className="rounded-xl border border-warning/40 bg-warning/5 p-4 flex items-start gap-3" data-testid="subscription-required-banner">
            <ShieldCheck className="h-5 w-5 text-warning mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm">{t('telemedActivateSubTitle')}</div>
              <div className="text-xs text-muted-foreground mt-1">{t('telemedActivateSubDesc')}</div>
              <Button onClick={onStartSubscription} disabled={subCheckingOut} className="mt-3" data-testid="activate-subscription-btn">
                {subCheckingOut ? t('processing') : t('telemedActivate49')}
              </Button>
            </div>
          </div>
        )}
        {active && expDate && (
          <div className="text-xs text-muted-foreground">
            {t('telemedExpiresOn')}: <span className="font-medium text-foreground">{expDate}</span>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="visit-price">{t('telemedVisitPrice')}</Label>
            <div className="relative">
              <Euro className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="visit-price"
                type="number"
                min={5}
                max={500}
                step={1}
                value={form.visit_price_eur}
                onChange={(e) => setForm({ ...form, visit_price_eur: e.target.value })}
                className="pl-9"
                data-testid="doctor-visit-price-input"
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{t('telemedPlatformFeeNote')}</p>
          </div>
          <div className="flex flex-col justify-between">
            <Label>{t('telemedAcceptingBookings')}</Label>
            <div className="flex items-center gap-2 mt-2 h-10">
              <Switch checked={form.accepting_bookings} onCheckedChange={(v) => setForm({ ...form, accepting_bookings: v })} data-testid="doctor-accepting-toggle" />
              <span className="text-sm text-muted-foreground">{form.accepting_bookings ? t('yes') : t('no')}</span>
            </div>
          </div>
        </div>
        <div>
          <Label htmlFor="consultation-bio">{t('telemedConsultationBio')}</Label>
          <Textarea
            id="consultation-bio"
            rows={2}
            maxLength={1000}
            placeholder={t('telemedConsultationBioPh')}
            value={form.consultation_bio}
            onChange={(e) => setForm({ ...form, consultation_bio: e.target.value })}
            data-testid="doctor-consultation-bio-input"
          />
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={onOpenAvailability} data-testid="open-availability-btn">
            <Settings2 className="h-4 w-4" /> {t('availabilityBtn')}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-doctor-telemed-profile-btn">
            {saving ? t('saving') : t('save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
